// Bytes <-> string for CoLOS .itf files.
// CoLOS writes UTF-16 LE with a BOM (FF FE); we always write that back.
// Pure module: no DOM, runs in Node and the browser.

function toBytes(bytes) {
  if (bytes instanceof Uint8Array) return bytes;
  if (bytes instanceof ArrayBuffer) return new Uint8Array(bytes);
  if (ArrayBuffer.isView(bytes)) return new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  throw new TypeError('decodeItf expects an ArrayBuffer or Uint8Array');
}

// Decode UTF-16 code units manually (chunked) so behaviour is identical
// everywhere and lone surrogates survive a decode/encode round trip.
// A UTF-16 payload must have an even byte count; a trailing odd byte means a truncated/damaged file.
function decodeUtf16(u8, start, bigEndian) {
  if ((u8.length - start) % 2 !== 0) throw new Error('File is damaged (odd byte count)');
  const units = (u8.length - start) >> 1;
  const parts = [];
  const CHUNK = 0x2000;
  for (let u = 0; u < units; u += CHUNK) {
    const n = Math.min(CHUNK, units - u);
    const codes = new Array(n);
    for (let k = 0; k < n; k++) {
      const p = start + (u + k) * 2;
      codes[k] = bigEndian ? (u8[p] << 8) | u8[p + 1] : u8[p] | (u8[p + 1] << 8);
    }
    parts.push(String.fromCharCode.apply(null, codes));
  }
  return parts.join('');
}

const decodeUtf8 = (u8) => new TextDecoder('utf-8', { ignoreBOM: true }).decode(u8);

// After a BOM has been consumed, a file written with a doubled BOM still starts with U+FEFF.
// Strip exactly one so the XML starts at '<'.
const stripOneBom = (text) => (text.charCodeAt(0) === 0xfeff ? text.slice(1) : text);

/**
 * @param {ArrayBuffer|Uint8Array} bytes
 * @returns {{ text: string, encoding: 'utf16le'|'utf16be'|'utf8' }}
 * @throws {Error} 'File is damaged (odd byte count)' for UTF-16 input with an odd number of bytes
 */
export function decodeItf(bytes) {
  const u8 = toBytes(bytes);
  if (u8.length >= 2 && u8[0] === 0xff && u8[1] === 0xfe) {
    return { text: stripOneBom(decodeUtf16(u8, 2, false)), encoding: 'utf16le' };
  }
  if (u8.length >= 2 && u8[0] === 0xfe && u8[1] === 0xff) {
    return { text: stripOneBom(decodeUtf16(u8, 2, true)), encoding: 'utf16be' };
  }
  if (u8.length >= 3 && u8[0] === 0xef && u8[1] === 0xbb && u8[2] === 0xbf) {
    return { text: stripOneBom(decodeUtf8(u8.subarray(3))), encoding: 'utf8' };
  }
  // BOM-less heuristic: XML starts with an ASCII '<' (or whitespace), so a UTF-16
  // file has a zero byte in the high half of the first code unit (UTF-8 text never contains NUL).
  if (u8.length >= 2) {
    if (u8[0] !== 0 && u8[1] === 0) return { text: decodeUtf16(u8, 0, false), encoding: 'utf16le' };
    if (u8[0] === 0 && u8[1] !== 0) return { text: decodeUtf16(u8, 0, true), encoding: 'utf16be' };
  }
  return { text: decodeUtf8(u8), encoding: 'utf8' };
}

/**
 * Always BOM (FF FE) + UTF-16 LE code units.
 * @param {string} text
 * @returns {Uint8Array}
 */
export function encodeItf(text) {
  const s = String(text);
  const out = new Uint8Array(2 + s.length * 2);
  out[0] = 0xff;
  out[1] = 0xfe;
  for (let i = 0, p = 2; i < s.length; i++, p += 2) {
    const c = s.charCodeAt(i);
    out[p] = c & 0xff;
    out[p + 1] = c >> 8;
  }
  return out;
}
